import { useRef, useState } from 'react';
import { Button, Field, Modal, Pill, Spinner, TextInput } from '@salonomy/ui';
import { useNoShowFee } from '../../data/payments.js';
import { useResolvedServicePolicy } from '../../data/policy.js';
import { useAuth } from '../../lib/auth.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { penceToPounds, poundsToPence } from '../till/money.js';

/** The no-show appointment the manager confirms a fee against (server facts the till already holds). */
export interface NoShowApptContext {
  appointmentId: string;
  serviceId: string;
  clientName: string;
  /** The frozen quoted price (a server number) — drives a PERCENT-fee suggestion only. */
  priceQuotedPence: number;
  /** Whether the appointment is actually marked `no_show`. The API REQUIRES this (409 not_no_show). */
  isNoShow: boolean;
}

/**
 * WS6 — charge a NO-SHOW FEE (manager-only). This is the DEPOSIT-FORFEIT / no-show path: it MINTS a new
 * charge against an appointment marked `no_show` — it does NOT refund or move an existing payment, so it
 * is the OPPOSITE of a deposit refund. The API 409s with `not_no_show` unless the appointment is already
 * marked no-show, so this surface gates on that and shows an honest note rather than firing a guaranteed
 * 409. LATE-CANCEL charging has no public route yet, so this is no-show-only by design.
 *
 * MONEY HONESTY: the resolved service policy only SUGGESTS an amount (a fixed fee directly; a percent fee
 * computed from the quoted price; a deposit forfeit the manager enters). The manager CONFIRMS the amount
 * and the SERVER is authoritative for what is charged — we never auto-compute the charge as authoritative.
 *
 * IDEMPOTENCY: the key is minted ONCE per intent and the button is gated on `isPending`, so a double-tap
 * replays the original charge and never double-charges.
 */
export function NoShowChargeModal({
  appt,
  onCharged,
  onClose,
}: {
  appt: NoShowApptContext;
  onCharged?: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const { isManager } = useAuth();
  const charge = useNoShowFee();
  // Mint the idempotency key ONCE per modal-open intent (stable across re-renders). A synchronous
  // double-tap then carries the SAME key, so the server REPLAYS the original charge instead of minting
  // a second one — `isPending`/`disabled` only flip on the next render commit, so the key (not the
  // button) is what makes a same-task double-fire safe.
  const idemKey = useRef(crypto.randomUUID()).current;
  // Read the resolved policy to SUGGEST an amount — gated to managers (it's a manager-only action anyway).
  const { data: policy, isLoading: policyLoading } = useResolvedServicePolicy(
    isManager ? appt.serviceId : undefined,
  );

  const noShowRule = policy?.rule.noShow;
  // A SUGGESTION only — never authoritative. Fixed ⇒ the rule's pence; percent ⇒ % of the quoted price;
  // forfeit_deposit/none ⇒ no auto figure (the manager enters it — we don't know the deposit here).
  const suggestedPence =
    noShowRule?.feeKind === 'fixed'
      ? noShowRule.amountPence
      : noShowRule?.feeKind === 'percent'
        ? Math.round((appt.priceQuotedPence * (noShowRule.percent ?? 0)) / 100)
        : null;

  const [amount, setAmount] = useState('');
  // Default the field to the suggestion once the policy loads (only if the manager hasn't typed yet).
  const [touched, setTouched] = useState(false);
  const effective = touched
    ? amount
    : suggestedPence != null
      ? penceToPounds(suggestedPence)
      : amount;

  const parsed = poundsToPence(effective);
  const canSubmit = isManager && appt.isNoShow && parsed != null && parsed > 0 && !charge.isPending;

  async function submit() {
    if (!canSubmit || parsed == null) return;
    try {
      // The idempotency key is minted ONCE per intent (above) — a double-tap reuses it, so the API
      // REPLAYS the original charge and never double-charges. The manager-confirmed amount is sent explicitly.
      await charge.mutateAsync({
        appointmentId: appt.appointmentId,
        amountPence: parsed,
        idempotencyKey: idemKey,
      });
      toast.success(`No-show fee of ${money(parsed)} charged`);
      onCharged?.();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not charge the no-show fee');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Charge no-show fee"
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {charge.isPending
              ? 'Charging…'
              : parsed != null && parsed > 0
                ? `Charge ${money(parsed)}`
                : 'Charge fee'}
          </Button>
        </>
      }
    >
      <div className="till__line muted" style={{ marginBottom: 12 }}>
        <span>{appt.clientName || 'Walk-in'} · quoted</span>
        <span className="mono">{money(appt.priceQuotedPence)}</span>
      </div>

      {!isManager ? (
        <div style={{ marginBottom: 12 }}>
          <Pill tone="danger">Manager only</Pill>
          <div className="muted" style={{ marginTop: 8, fontSize: 13.5 }}>
            Charging a no-show fee is a manager action. Ask an owner or manager.
          </div>
        </div>
      ) : !appt.isNoShow ? (
        <div style={{ marginBottom: 12 }}>
          <Pill tone="danger">Not marked no-show</Pill>
          <div className="muted" style={{ marginTop: 8, fontSize: 13.5, lineHeight: 1.5 }}>
            This appointment must be marked <strong>no-show</strong> in the diary first — the server
            won&rsquo;t charge a no-show fee otherwise. Late-cancel charging has no public route
            yet, so this surface is no-show-only.
          </div>
        </div>
      ) : (
        <>
          {policyLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Spinner size={16} />
              <span className="muted" style={{ fontSize: 13 }}>
                Reading the no-show policy…
              </span>
            </div>
          ) : policy ? (
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
              <div style={{ marginBottom: 4 }}>{policy.noShowText}</div>
              {noShowRule?.feeKind === 'forfeit_deposit' ? (
                <div>
                  This policy <strong>forfeits the deposit</strong> already paid. Enter the amount
                  to forfeit below — we don&rsquo;t auto-charge it for you.
                </div>
              ) : suggestedPence != null ? (
                <div>
                  Policy suggests <strong>{money(suggestedPence)}</strong> — confirm or adjust it
                  below.
                </div>
              ) : (
                <div>No fixed fee in policy — enter the amount to charge below.</div>
              )}
            </div>
          ) : null}

          <Field
            label="Fee to charge (£)"
            hint="You confirm the amount; the payments server is authoritative for what's charged."
          >
            <TextInput
              inputMode="decimal"
              value={effective}
              onChange={(e) => {
                setTouched(true);
                setAmount(e.target.value);
              }}
              placeholder="0.00"
            />
          </Field>

          <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 4 }}>
            This <strong>mints a new charge</strong> against the no-show appointment — it&rsquo;s
            not a refund and doesn&rsquo;t move an existing payment. The policy figure is only a
            suggestion; nothing is computed as authoritative on this screen.
          </div>
        </>
      )}
    </Modal>
  );
}
